import React, { useMemo } from 'react';
import { withStyles, Icon, BodyText } from 'vopay';
import { useTranslation } from 'react-i18next';
import styles from './Complete.module.scss';
import { Title } from '../../UI';
import { useToken } from '@/providers/Token.provider';
import BrandingLogo from './BrandingLogo/BrandingLogo';

const classes = withStyles(styles);

interface DetailRow {
  label: React.ReactNode;
  value: React.ReactNode;
}

interface CompleteProps {
  title?: string; // Optional custom title, defaults to "Transaction Successful"
  amount: string;
  username: string;
  transactionType?: 'sent' | 'received'; // 'sent' shows "to", 'received' shows "from"
  transactionText?: string; // Optional custom transaction text
  subText?: string; // Optional subtext below the title
  showAmounts?: boolean; // Boolean to control whether amounts are displayed
  details: DetailRow[];
  children?: React.ReactNode; // Optional children for additional content
}

const Complete: React.FC<CompleteProps> = ({
  title: titleProp,
  amount,
  username,
  transactionType = 'sent',
  transactionText,
  showAmounts = true,
  subText,
  details,
  children,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Complete' });
  const { token } = useToken();
  const title = titleProp || t('transactionSuccessful', { defaultValue: 'Transaction Successful' });

  const recipientText = useMemo(() => {
    const preposition = transactionType === 'sent' ? t('to') : t('from');
    return (
      <>
        {preposition} <strong>{username}</strong>
      </>
    );
  }, [transactionType, username, t]);

  return (
    <div className={classes('container')}>
      <div className={classes('content')}>
        {/* Success Icon */}
        <div className={classes('iconContainer')}>
          <div className={classes('successIcon')}>
            <Icon icon="check" height="34px" width="34px" color="primary-light" />
          </div>
          <Title level="3" className={classes('title')}>
            {title}
          </Title>
          {subText && <BodyText className={classes('subtext')}>{subText}</BodyText>}
        </div>

        {/* Success Message */}
        {showAmounts && (
          <div className={classes('messageContainer')}>
            {transactionText && <div className={classes('transactionType')}>{transactionText}</div>}
            <Title level="1">{amount}</Title>
            <BodyText size="sm">{recipientText}</BodyText>
          </div>
        )}

        {/* Transaction Details */}
        {details.length > 0 && (
          <div className={classes('detailsContainer')}>
            {details.map((detail, index) => (
              <div key={index} className={classes('detailRow')}>
                <BodyText weight="md">{detail.label}</BodyText>
                <BodyText className={classes('detailValue')}>{detail.value}</BodyText>
              </div>
            ))}
          </div>
        )}
        {children && <div>{children}</div>}
      </div>

      {/* Footer */}
      <div className={classes('footer')}>
        <BodyText size="sm" weight="md" className={classes('poweredBy')}>
          {t('poweredBy')}
          {token?.BrandingDetails?.Logo && <BrandingLogo src={token?.BrandingDetails?.Logo} />}
          {!token?.BrandingDetails?.Logo && <Icon icon="vLogoMark" height="20px" width="auto" color="primary-dark" />}
        </BodyText>
      </div>
    </div>
  );
};

export default Complete;
